import { lazy, Suspense } from "react"
import { Outlet } from "react-router-dom"
import { isJobDetails } from "@/src/utils/storage-available"

import { CONFIG } from "src/config-global"
import { DashboardLayout } from "src/layouts/dashboard"

import { LoadingScreen } from "src/components/loading-screen"

import { AuthGuard } from "src/auth/guard"

import { paths } from "../paths"

// ----------------------------------------------------------------------

const CandidatesPage = lazy(() => import("@/src/pages/dashboard/candidatesPage/candidatesPage"))
const CandidatesAiProcessingPage = lazy(
    () => import("@/src/pages/dashboard/candidatesAiProcessingPage/candidatesAiProcessingPage")
)

const InterviewsPage = lazy(() => import("src/pages/dashboard/interviewsPage/interviewsPage"))
const ClientsPage = lazy(() => import("src/pages/dashboard/clientsPage/clientsPage"))
const MandatesPage = lazy(() => import("src/pages/dashboard/mandatesPage/mandatesPage"))
const MandatesAiProcessingPage = lazy(
    () => import("src/pages/dashboard/mandatesAiProcessingPage/mandatesAiProcessingPage")
)
const SelectionsPage = lazy(() => import("src/pages/dashboard/selectionsPage/selectionsPage"))
const BulkUploadsPage = lazy(() => import("src/pages/dashboard/bulkUploadPage/bulkUploadPage"))
const UserManagementPage = lazy(() => import("src/pages/dashboard/userManagementPage/userManagementPage"))
const InterviewsForm = lazy(() => import("src/pages/dashboard/newInterviewsPage"))
const LeadsForm = lazy(() => import("src/pages/dashboard/newLeadsPage"))
const ClientsForm = lazy(() => import("src/pages/dashboard/newClientsPage"))
const MandatesForm = lazy(() => import("@/src/pages/dashboard/newMandatesPage/mandatesFormPage"))
const SelectionForm = lazy(() => import("src/pages/dashboard/newSelectionPage"))
const ClientDetailsPage = lazy(() => import("src/pages/dashboard/clientsPage/clientsDetailsPage"))
const MandateDetailsPage = lazy(() => import("src/pages/dashboard/mandatesPage/mandatesDetailsPage"))
const SelectionDetailsPage = lazy(() => import("src/pages/dashboard/selectionsPage/selectionsDetailsPage"))
const JobDetailsPage = lazy(() => import("src/pages/dashboard/jobDetailsPage/jobDetailsPage"))
const OnboardingPage = lazy(() => import("src/pages/dashboard/onboarding/onboarding"))
const PublicApplyJobV2Page = lazy(() => import("src/pages/dashboard/publicApplyJobV2/publicApplyJobV2Page"))
const GenerateOtpPage = lazy(() => import("src/pages/dashboard/generateOtpPage/generateOtpPage"))
const CreditsPage = lazy(() => import("src/pages/dashboard/creditsPage/creditsPage"))
// ----------------------------------------------------------------------

const LayoutContent = () => {
    const layout = (
        <DashboardLayout>
            <Suspense fallback={<LoadingScreen />}>
                <Outlet />
            </Suspense>
        </DashboardLayout>
    )
    return isJobDetails() || CONFIG.auth.skip ? <>{layout}</> : <AuthGuard>{layout}</AuthGuard>
}

export const dashboardRoutes = (isAiProcessing: boolean, disableAddCandidates: boolean = false) => [
    {
        path: "dashboard",
        element: LayoutContent(),
        children: [
            { element: isAiProcessing ? <CandidatesAiProcessingPage /> : <CandidatesPage />, index: true },
            { path: paths.dashboard.interviews, element: <InterviewsPage /> },
            { path: paths.dashboard.interviewForm, element: <InterviewsForm /> },
            { path: paths.dashboard.leadForm, element: <LeadsForm /> },
            { path: paths.dashboard.clients, element: <ClientsPage /> },
            { path: paths.dashboard.selections, element: <SelectionsPage /> },
            {
                path: paths.dashboard.mandates,
                element: isAiProcessing ? <MandatesAiProcessingPage /> : <MandatesPage />,
            },
            ...(!disableAddCandidates ? [{ path: paths.dashboard.onboarding, element: <OnboardingPage /> }] : []),
            { path: paths.dashboard.clientForm, element: <ClientsForm /> },
            { path: paths.dashboard.selectionForm, element: <SelectionForm /> },
            {
                path: paths.dashboard.mandateForm,
                element: <MandatesForm />,
            },
            ...(!disableAddCandidates
                ? [
                      {
                          path: paths.dashboard.bulkUpload,
                          element: <BulkUploadsPage />,
                      },
                  ]
                : []),
            { path: paths.dashboard.userManagement, element: <UserManagementPage /> },
            { path: paths.dashboard.publicApplyWeb, element: <PublicApplyJobV2Page /> },
            { path: paths.dashboard.publicApplyPhone, element: <PublicApplyJobV2Page /> },
            { path: paths.dashboard.jobOverview, element: <JobDetailsPage /> },
            { path: paths.dashboard.client.root, element: <ClientDetailsPage /> },
            { path: paths.dashboard.mandate.root, element: <MandateDetailsPage /> },
            { path: paths.dashboard.selection.root, element: <SelectionDetailsPage /> },
            { path: paths.dashboard.credits.root, element: <CreditsPage /> },
            { path: paths.dashboard.lead.generateOtp, element: <GenerateOtpPage /> },
        ],
    },
]
